import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { invoicesApi } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { FileCheck2, Plus, Layers, Printer, Search } from 'lucide-react';
import { toast } from 'sonner';
import InvoiceStatusBadge, { openBlob } from '../components/InvoiceStatusBadge';
import InvoiceFromDdtDialog from '../components/InvoiceFromDdtDialog';

const eur = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);

const InvoicesListPage = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [search, setSearch] = useState('');
  const [fromDdtOpen, setFromDdtOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    invoicesApi.getAll({ year }).then((r) => setList(r.data))
      .catch(() => toast.error('Errore nel caricamento delle fatture')).finally(() => setLoading(false));
  }, [year]);

  const q = search.trim().toLowerCase();
  const rows = list.filter((i) => !q || String(i.number).includes(q) || (i.client_label || '').toLowerCase().includes(q));

  const pdf = async (inv) => {
    try { const r = await invoicesApi.getPdf(inv.id); openBlob(r.data, 'application/pdf'); }
    catch { toast.error('Errore nella generazione PDF'); }
  };

  return (
    <div data-testid="invoices-list-page" className="space-y-6">
      <InvoiceFromDdtDialog open={fromDdtOpen} onOpenChange={setFromDdtOpen} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-['Manrope']">Fatture elettroniche</h1>
          <p className="text-muted-foreground text-sm">Crea le fatture dai DDT o da zero e inviale allo SDI tramite Aruba</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="invoice-from-ddt-btn" variant="outline" onClick={() => setFromDdtOpen(true)}>
            <Layers className="w-4 h-4 mr-2" /> FATTURA DA DDT
          </Button>
          <Link to="/fatture/new">
            <Button data-testid="new-invoice-btn" className="btn-primary"><Plus className="w-4 h-4 mr-2" /> NUOVA FATTURA</Button>
          </Link>
        </div>
      </div>

      <Card className="card-swiss">
        <CardContent className="pt-6 flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input data-testid="search-invoice-input" placeholder="Cerca per numero o cliente..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
          </div>
          <Input data-testid="invoice-year-input" type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value) || new Date().getFullYear())} className="w-28" />
        </CardContent>
      </Card>

      <Card className="card-swiss">
        <CardContent className="p-0">
          {loading ? <div className="p-8 text-center text-muted-foreground">Caricamento...</div>
            : rows.length === 0 ? (
              <div data-testid="invoices-empty" className="text-center py-12 text-muted-foreground">
                <FileCheck2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nessuna fattura per il {year}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="table-header-swiss w-[90px]">N°</TableHead>
                    <TableHead className="table-header-swiss w-[110px]">Data</TableHead>
                    <TableHead className="table-header-swiss">Cliente</TableHead>
                    <TableHead className="table-header-swiss w-[110px]">Tipo</TableHead>
                    <TableHead className="table-header-swiss w-[130px] text-right">Totale</TableHead>
                    <TableHead className="table-header-swiss w-[150px]">Stato</TableHead>
                    <TableHead className="table-header-swiss w-[60px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((inv) => (
                    <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`} className="table-row-swiss cursor-pointer" onClick={() => navigate(`/fatture/${inv.id}`)}>
                      <TableCell className="table-cell-swiss font-['JetBrains_Mono'] font-semibold">{inv.number}/{inv.year}</TableCell>
                      <TableCell className="table-cell-swiss">{new Date(inv.date).toLocaleDateString('it-IT')}</TableCell>
                      <TableCell className="table-cell-swiss">{inv.client_label}</TableCell>
                      <TableCell className="table-cell-swiss text-xs text-muted-foreground">{inv.ddt_refs?.length ? `Differita (${inv.ddt_refs.length} DDT)` : 'Immediata'}</TableCell>
                      <TableCell className="table-cell-swiss text-right font-['JetBrains_Mono']">{eur(inv.totale)}</TableCell>
                      <TableCell className="table-cell-swiss"><InvoiceStatusBadge status={inv.status} testId={`invoice-status-${inv.id}`} /></TableCell>
                      <TableCell className="table-cell-swiss" onClick={(e) => e.stopPropagation()}>
                        <Button data-testid={`invoice-pdf-${inv.id}`} variant="ghost" size="icon" className="h-8 w-8" title="PDF di cortesia" onClick={() => pdf(inv)}>
                          <Printer className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InvoicesListPage;
